import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Home from "./pages/Home";
import BookingPage from "./pages/BookingPage";
import CalendarPage from "./pages/CalendarPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminServices from "./pages/admin/AdminServices";
import AdminProfessionals from "./pages/admin/AdminProfessionals";
import AdminAppointments from "./pages/admin/AdminAppointments";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminCheckout from "./pages/admin/AdminCheckout";
import AdminCommissions from "./pages/admin/AdminCommissions";
import AdminAttendance from "./pages/admin/AdminAttendance";
import StaffClockIn from "./pages/admin/StaffClockIn";
import AdminClients from "./pages/admin/AdminClients";
import AdminStaff from "./pages/admin/AdminStaff";
import AdminReports from "./pages/admin/AdminReports";
import AdminTutorial from "./pages/admin/AdminTutorial";
import AdminAcademy from "./pages/admin/AdminAcademy";
import AdminWeeklyCalendar from "./pages/admin/AdminWeeklyCalendar";
import AdminQueuePanel from "./pages/admin/AdminQueuePanel";
import AdminMedia from "./pages/admin/AdminMedia";
import AdminUpgrade from "./pages/admin/AdminUpgrade";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminStoreOrders from "./pages/admin/AdminStoreOrders";
import AdminBranches from "./pages/admin/AdminBranches";
import AdminPackages from "./pages/admin/AdminPackages";
import AdminPromotions from "./pages/admin/AdminPromotions";
import AdminInstagram from "./pages/admin/AdminInstagram";
import AdminAiConversations from "./pages/admin/AdminAiConversations";
import AdminKnowledge from "./pages/admin/AdminKnowledge";
import AdminAgentTest from "./pages/admin/AdminAgentTest";
import AdminPaymentProofs from "./pages/admin/AdminPaymentProofs";
import FiscalPage from "./pages/FiscalPage";
import WhatsAppLogsPage from "./pages/WhatsAppLogsPage";
import StaffProfile from "./pages/admin/StaffProfile";
import MyAppointments from "./pages/MyAppointments";
import OnboardingPage from "./pages/OnboardingPage";
import PricingPage from "./pages/PricingPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import SuperAdminSalonDetail from "./pages/SuperAdminSalonDetail";
import SuperAdminAiAgents from "./pages/SuperAdminAiAgents";
import SalonPublicPage from "./pages/SalonPublicPage";
import SalonStorePage from "./pages/SalonStorePage";
import CustomStoreDomainPage from "./pages/CustomStoreDomainPage";
import BranchPublicPage from "./pages/BranchPublicPage";
import BranchStorePage from "./pages/BranchStorePage";
import StaffMyCommissions from "./pages/admin/MyCommissions";
import About from "./pages/About";
import ContractPage from "./pages/ContractPage";
import SaasContractPage from "./pages/SaasContractPage";
import QueueStatusPage from "./pages/QueueStatusPage";
import ClientCheckInPage from "./pages/ClientCheckInPage";
import DashboardLayout from "./components/DashboardLayout";
import { FeatureGate } from "./components/FeatureGate";
import AdminLoginPage from "./pages/AdminLoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import TeamLoginPage from "./pages/TeamLoginPage";
import StaffAccessPage from "./pages/StaffAccessPage";
import AdminCancellationRequests from "./pages/admin/AdminCancellationRequests";
import AdminWaitlist from "./pages/admin/AdminWaitlist";
import AdminRecurrences from "./pages/admin/AdminRecurrences";
import AdminFinancial from "./pages/admin/AdminFinancial";
import AdminClientRecords from "./pages/admin/AdminClientRecords";
import AdminCrm from "./pages/admin/AdminCrm";
import AdminLoyalty from "./pages/admin/AdminLoyalty";
import ClientPortal from "./pages/ClientPortal";
import AdminAutomationMonitor from "./pages/admin/AdminAutomationMonitor";
import AdminPrivacySecurity from "./pages/admin/AdminPrivacySecurity";
import AdminIntegrations from "./pages/admin/AdminIntegrations";
import AdminExecutiveIntelligence from "./pages/admin/AdminExecutiveIntelligence";
import PlatformCommercialWorkspace from "./pages/PlatformCommercialWorkspace";
import { getCustomStoreDomainFromLocation } from "./lib/storeDomain";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import DataDeletionPage from "./pages/DataDeletionPage";

// AdminRoutes uses relative paths because it's rendered inside a `nest` route
function AdminRoutes() {
  return (
    <DashboardLayout>
      <Switch>
        {/* These paths are relative to /admin (the nest base) */}
        <Route path="/" component={AdminDashboard} />
        <Route path="/dashboard" component={AdminDashboard} />
        <Route path="/services" component={AdminServices} />
        <Route path="/professionals">
          {() => (
            <FeatureGate feature="hasOperations">
              <AdminProfessionals />
            </FeatureGate>
          )}
        </Route>
        <Route path="/appointments" component={AdminAppointments} />
        <Route path="/settings" component={AdminSettings} />
        <Route path="/reviews">
          {() => (
            <FeatureGate feature="hasCrm">
              <AdminReviews />
            </FeatureGate>
          )}
        </Route>
        <Route path="/checkout">
          {() => (
            <FeatureGate feature="hasCheckout">
              <AdminCheckout />
            </FeatureGate>
          )}
        </Route>
        <Route path="/my-commissions" component={StaffMyCommissions} />
        <Route path="/commissions">
          {() => (
            <FeatureGate feature="hasCommissions">
              <AdminCommissions />
            </FeatureGate>
          )}
        </Route>
        <Route path="/attendance">
          {() => (
            <FeatureGate feature="hasOperations">
              <AdminAttendance />
            </FeatureGate>
          )}
        </Route>
        <Route path="/clock-in" component={StaffClockIn} />
        <Route path="/clients">
          {() => (
            <FeatureGate feature="hasCrm">
              <AdminClients />
            </FeatureGate>
          )}
        </Route>
        <Route path="/staff">
          {() => (
            <FeatureGate feature="hasOperations">
              <AdminStaff />
            </FeatureGate>
          )}
        </Route>
        <Route path="/reports">
          {() => (
            <FeatureGate feature="hasAdvancedReports">
              <AdminReports />
            </FeatureGate>
          )}
        </Route>
        <Route path="/intelligence">
          {() => (
            <FeatureGate feature="hasIntelligence">
              <AdminExecutiveIntelligence />
            </FeatureGate>
          )}
        </Route>
        <Route path="/tutorial">
          {() => (
            <FeatureGate feature="hasAcademy">
              <AdminTutorial />
            </FeatureGate>
          )}
        </Route>
        <Route path="/academia">
          {() => (
            <FeatureGate feature="hasAcademy">
              <AdminAcademy />
            </FeatureGate>
          )}
        </Route>
        <Route path="/weekly-calendar" component={AdminWeeklyCalendar} />
        <Route path="/painel-chamada">
          {() => (
            <FeatureGate feature="hasPanelChamada">
              <AdminQueuePanel />
            </FeatureGate>
          )}
        </Route>
        <Route path="/media">
          {() => (
            <FeatureGate feature="hasMediaPromocional">
              <AdminMedia />
            </FeatureGate>
          )}
        </Route>
        <Route path="/upgrade" component={AdminUpgrade} />
        <Route path="/payments">
          {() => (
            <FeatureGate feature="hasFinancial">
              <AdminPayments />
            </FeatureGate>
          )}
        </Route>
        <Route path="/products">
          {() => (
            <FeatureGate feature="hasInventory">
              <AdminProducts />
            </FeatureGate>
          )}
        </Route>
        <Route path="/store-orders">
          {() => (
            <FeatureGate feature="hasInventory">
              <AdminStoreOrders />
            </FeatureGate>
          )}
        </Route>
        <Route path="/branches">
          {() => (
            <FeatureGate feature="hasBranches">
              <AdminBranches />
            </FeatureGate>
          )}
        </Route>
        <Route path="/packages">
          {() => (
            <FeatureGate feature="hasCrm">
              <AdminPackages />
            </FeatureGate>
          )}
        </Route>
        <Route path="/promotions">
          {() => (
            <FeatureGate feature="hasCrm">
              <AdminPromotions />
            </FeatureGate>
          )}
        </Route>
        <Route path="/ai-conversations">
          {() => (
            <FeatureGate feature="hasAutomation">
              <AdminAiConversations />
            </FeatureGate>
          )}
        </Route>
        <Route path="/knowledge">
          {() => (
            <FeatureGate feature="hasAutomation">
              <AdminKnowledge />
            </FeatureGate>
          )}
        </Route>
        <Route path="/agent-test">
          {() => (
            <FeatureGate feature="hasAutomation">
              <AdminAgentTest />
            </FeatureGate>
          )}
        </Route>
        <Route path="/payment-proofs">
          {() => (
            <FeatureGate feature="hasFinancial">
              <AdminPaymentProofs />
            </FeatureGate>
          )}
        </Route>
        <Route path="/fiscal">
          {() => (
            <FeatureGate feature="hasFiscal">
              <FiscalPage />
            </FeatureGate>
          )}
        </Route>
        <Route path="/whatsapp-logs">
          {() => (
            <FeatureGate feature="hasAutomation">
              <WhatsAppLogsPage />
            </FeatureGate>
          )}
        </Route>
        <Route path="/instagram">
          {() => (
            <FeatureGate feature="hasIntegrations">
              <AdminInstagram />
            </FeatureGate>
          )}
        </Route>
        <Route path="/meu-perfil" component={StaffProfile} />
        <Route path="/cancellation-requests">
          {() => (
            <FeatureGate feature="hasCrm">
              <AdminCancellationRequests />
            </FeatureGate>
          )}
        </Route>
        <Route path="/waitlist">
          {() => (
            <FeatureGate feature="hasCrm">
              <AdminWaitlist />
            </FeatureGate>
          )}
        </Route>
        <Route path="/recurrences">
          {() => (
            <FeatureGate feature="hasCrm">
              <AdminRecurrences />
            </FeatureGate>
          )}
        </Route>
        <Route path="/financial">
          {() => (
            <FeatureGate feature="hasFinancial">
              <AdminFinancial />
            </FeatureGate>
          )}
        </Route>
        <Route path="/client-records">
          {() => (
            <FeatureGate feature="hasCrm">
              <AdminClientRecords />
            </FeatureGate>
          )}
        </Route>
        <Route path="/crm">
          {() => (
            <FeatureGate feature="hasCrm">
              <AdminCrm />
            </FeatureGate>
          )}
        </Route>
        <Route path="/loyalty">
          {() => (
            <FeatureGate feature="hasCrm">
              <AdminLoyalty />
            </FeatureGate>
          )}
        </Route>
        <Route path="/automation-monitor">
          {() => (
            <FeatureGate feature="hasAutomation">
              <AdminAutomationMonitor />
            </FeatureGate>
          )}
        </Route>
        <Route path="/privacy-security">
          {() => (
            <FeatureGate feature="hasSecurity">
              <AdminPrivacySecurity />
            </FeatureGate>
          )}
        </Route>
        <Route path="/integrations">
          {() => (
            <FeatureGate feature="hasIntegrations">
              <AdminIntegrations />
            </FeatureGate>
          )}
        </Route>
        <Route path="/team">
          {() => (
            <FeatureGate feature="hasOperations">
              <AdminStaff />
            </FeatureGate>
          )}
        </Route>
        <Route component={NotFound} />
      </Switch>
    </DashboardLayout>
  );
}

function AdminDashboardRoute() {
  return (
    <DashboardLayout>
      <AdminDashboard />
    </DashboardLayout>
  );
}

function PublicHomeRoute() {
  const storeDomain = getCustomStoreDomainFromLocation();
  return storeDomain ? (
    <CustomStoreDomainPage domain={storeDomain} />
  ) : (
    <Home />
  );
}

function AppRouter() {
  return (
    <Switch>
      {/* Specific routes first — wouter's Switch matches the FIRST route that fits.
          "/" matches any path that starts with "/", so it must come LAST. */}
      <Route path="/login" component={AdminLoginPage} />
      <Route path="/team-login" component={TeamLoginPage} />
      <Route path="/staff-access" component={StaffAccessPage} />
      <Route path="/forgot-password" component={ForgotPasswordPage} />
      <Route path="/reset-password" component={ResetPasswordPage} />
      <Route path="/booking" component={BookingPage} />
      <Route path="/calendar" component={CalendarPage} />
      <Route path="/my-appointments" component={MyAppointments} />
      <Route path="/portal" component={ClientPortal} />
      <Route path="/onboarding" component={OnboardingPage} />
      <Route path="/pricing" component={PricingPage} />
      <Route path="/superadmin/agentes/teste" component={AdminAgentTest} />
      <Route path="/superadmin" component={SuperAdminPage} />
      <Route path="/superadmin/saloes/:id" component={SuperAdminSalonDetail} />
      <Route path="/superadmin/agentes" component={SuperAdminAiAgents} />
      <Route
        path="/platform-workspace"
        component={PlatformCommercialWorkspace}
      />
      <Route path="/filial/:branchSlug/loja" component={BranchStorePage} />
      <Route path="/filial/:branchSlug" component={BranchPublicPage} />
      <Route path="/s/:slug/loja">{() => <SalonStorePage />}</Route>
      <Route path="/s/:slug" component={SalonPublicPage} />
      <Route path="/my-commissions" component={StaffMyCommissions} />
      <Route path="/sobre" component={About} />
      <Route path="/contrato" component={ContractPage} />
      <Route path="/contrato-saas" component={SaasContractPage} />
      <Route path="/politica-de-privacidade" component={PrivacyPolicyPage} />
      <Route path="/exclusao-de-dados" component={DataDeletionPage} />
      <Route path="/fila/:salonId" component={QueueStatusPage} />
      <Route path="/checkin/:salonId" component={ClientCheckInPage} />
      <Route path="/404" component={NotFound} />
      <Route path="/admin/dashboard" component={AdminDashboardRoute} />
      {/* nest prop: AdminRoutes receives all /admin/* paths with relative sub-routing */}
      <Route path="/admin" nest component={AdminRoutes} />
      {/* "/" must be last so it doesn't swallow all other routes */}
      <Route path={/^\/$/} component={PublicHomeRoute} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster richColors position="top-right" />
          <AppRouter />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
